import React from "react";
import { Link } from "react-router-dom";

/**
 * Trophy – gull/sølv/bronse-medalje for vinnende innhold.
 *
 * Bruk:
 *   <Trophy place={1} contestTitle="Månedens bilde – mai 2026" contestId="abc" />
 *   <Trophy place={2} variant="badge" />
 *   <Trophy place={3} variant="corner" />  ← absolutt-posisjonert til hjørnet
 */

const STYLES = {
  1: { name: "Gull", color: "#D4A574", bg: "rgba(212,165,116,0.18)", border: "rgba(212,165,116,0.55)" },
  2: { name: "Sølv", color: "#C7CBD1", bg: "rgba(199,203,209,0.16)", border: "rgba(199,203,209,0.50)" },
  3: { name: "Bronse", color: "#B26A3F", bg: "rgba(178,106,63,0.18)", border: "rgba(178,106,63,0.55)" },
};

function MedalSVG({ place, size = 18 }) {
  const s = STYLES[place] || STYLES[3];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      {/* Bånd */}
      <path d="M8 1 L4 8 L8 12 L12 8 Z" fill={s.color} opacity="0.55" />
      <path d="M16 1 L20 8 L16 12 L12 8 Z" fill={s.color} opacity="0.55" />
      {/* Medalje */}
      <circle cx="12" cy="15" r="7" fill={s.color} stroke={s.color} strokeWidth="1" />
      <circle cx="12" cy="15" r="5" fill="rgba(0,0,0,0.25)" />
      <text
        x="12" y="18.5"
        textAnchor="middle"
        fontSize="7.5"
        fontWeight="700"
        fill="#fff"
        style={{ fontFamily: "'Fraunces', serif", fontStyle: "italic" }}
      >
        {place}
      </text>
    </svg>
  );
}

export default function Trophy({
  place,
  contestTitle = "",
  contestId = null,
  variant = "badge",  // "badge" | "corner" | "inline" | "icon"
  size = 18,
  className = "",
}) {
  if (!place || place < 1 || place > 3) return null;
  const s = STYLES[place];
  const tooltip = `${s.name} – ${contestTitle || "konkurranse"}`;

  const inner = (
    <span
      title={tooltip}
      aria-label={tooltip}
      className={`inline-flex items-center gap-1.5 ${className}`}
      data-testid={`trophy-place-${place}`}
    >
      <MedalSVG place={place} size={size} />
      {variant === "inline" && (
        <span
          className="font-meta text-[10px] uppercase tracking-[0.12em] whitespace-nowrap"
          style={{ color: s.color }}
        >
          {s.name}
        </span>
      )}
    </span>
  );

  if (variant === "corner") {
    const node = (
      <span
        title={tooltip}
        aria-label={tooltip}
        className="inline-flex items-center justify-center"
        style={{
          width: size + 10,
          height: size + 10,
          borderRadius: 999,
          background: s.bg,
          border: `1px solid ${s.border}`,
          backdropFilter: "blur(4px)",
        }}
        data-testid={`trophy-corner-${place}`}
      >
        <MedalSVG place={place} size={size} />
      </span>
    );
    if (contestId) {
      return (
        <Link
          to={`/konkurranser/${contestId}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-block"
        >
          {node}
        </Link>
      );
    }
    return node;
  }

  if (variant === "badge") {
    const node = (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-1"
        style={{
          background: s.bg,
          border: `1px solid ${s.border}`,
          color: s.color,
        }}
        title={tooltip}
        aria-label={tooltip}
        data-testid={`trophy-badge-${place}`}
      >
        <MedalSVG place={place} size={size - 2} />
        <span className="font-meta text-[10px] uppercase tracking-[0.12em] whitespace-nowrap">
          {s.name}{contestTitle ? ` · ${contestTitle}` : ""}
        </span>
      </span>
    );
    return contestId ? (
      <Link to={`/konkurranser/${contestId}`} className="hover:opacity-80 transition">
        {node}
      </Link>
    ) : node;
  }

  // inline / icon
  return contestId ? (
    <Link to={`/konkurranser/${contestId}`} className="hover:opacity-80 transition">
      {inner}
    </Link>
  ) : inner;
}

/**
 * TrophyShelf – «Trofé-skap» for profil-siden.
 * Viser alle vinst-plasseringer for en bruker.
 */
export function TrophyShelf({ trophies = [], compact = false }) {
  if (!trophies || trophies.length === 0) return null;
  const total = trophies.length;
  const gold = trophies.filter((t) => t.place === 1).length;
  const silver = trophies.filter((t) => t.place === 2).length;
  const bronze = trophies.filter((t) => t.place === 3).length;

  return (
    <div className="panel" data-testid="trophy-shelf">
      <div className="panel-heading flex items-center justify-between">
        <span>Trofé-skap</span>
        <span className="font-meta text-[10px] text-[var(--cream-soft)]">
          på pallen {total}
        </span>
      </div>

      {/* Sammendrag */}
      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-white/5">
        {gold > 0 && (
          <span className="inline-flex items-center gap-1 text-xs" style={{ color: STYLES[1].color }}>
            <MedalSVG place={1} size={14} /> {gold}
          </span>
        )}
        {silver > 0 && (
          <span className="inline-flex items-center gap-1 text-xs" style={{ color: STYLES[2].color }}>
            <MedalSVG place={2} size={14} /> {silver}
          </span>
        )}
        {bronze > 0 && (
          <span className="inline-flex items-center gap-1 text-xs" style={{ color: STYLES[3].color }}>
            <MedalSVG place={3} size={14} /> {bronze}
          </span>
        )}
      </div>

      {/* Liste */}
      <ul className="space-y-2">
        {trophies.slice(0, compact ? 5 : 20).map((t, i) => (
          <li key={`${t.contest_id}-${i}`}>
            <Link
              to={`/konkurranser/${t.contest_id}`}
              className="flex items-start gap-2 group"
            >
              <span className="mt-0.5 shrink-0">
                <MedalSVG place={t.place} size={16} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-[var(--cream)] group-hover:text-[var(--magenta)] transition truncate">
                  {t.contest_title}
                </span>
                <span className="block font-meta text-[10px] text-[var(--cream-soft)] uppercase tracking-[0.1em]">
                  {STYLES[t.place].name} · {t.contest_type}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {trophies.length > (compact ? 5 : 20) && (
        <div className="mt-3 pt-2 border-t border-white/5 text-center">
          <span className="font-meta text-[10px] text-[var(--cream-soft)]">
            + {trophies.length - (compact ? 5 : 20)} flere
          </span>
        </div>
      )}
    </div>
  );
}
